import React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  Alert,
  Grid,
  DialogContent,
} from "@mui/material";
import {
  DirectionsBus,
  LocationOn,
  Speed,
  Person,
  Schedule,
  Error,
  CheckCircle,
  Warning,
  Info,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { useAuth } from "../../hooks/useAuth";

// InfoRow Component - for displaying information in a row format
const InfoRow = ({ icon, label, value }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={2}
    sx={{
      py: 1,
      borderBottom: "1px solid rgba(255,255,255,0.1)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.1)",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value || "N/A"}</Typography>
    </Box>
  </Box>
);

// StatCard Component - for displaying statistics in a card format
const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Card
      sx={{
        backgroundColor: colors.primary[400],
        padding: "20px",
        transition: "transform 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
        },
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: colors.grey[100],
              mb: "4px",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: color || colors.greenAccent[500] }}
          >
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: color || colors.greenAccent[500],
            borderRadius: "50%",
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {React.cloneElement(icon, {
            sx: { color: colors.primary[400] },
          })}
        </Box>
      </Box>
    </Card>
  );
};

// Helper function to calculate total trips with null safety
const calculateTotalTrips = (vehicles) => {
  try {
    if (!vehicles) return 0;

    const verifiedTrips = (vehicles.verifiedVehicles || []).reduce(
      (total, vehicle) => total + (vehicle?.Trip?.length || 0),
      0
    );

    const unverifiedTrips = (vehicles.unverifiedVehicles || []).reduce(
      (total, vehicle) => total + (vehicle?.Trip?.length || 0),
      0
    );

    return verifiedTrips + unverifiedTrips;
  } catch (error) {
    console.error("Error calculating total trips:", error);
    return 0;
  }
};

const Vehicles = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { user, vehicleApi } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vehicles, setVehicles] = useState({
    verifiedVehicles: [],
    unverifiedVehicles: [],
  });
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const ownerId = user?._id;

      if (!ownerId) {
        setError("User ID not found. Please login again.");
        setLoading(false);
        return;
      }

      // Use the API instance from useAuth
      const response = await vehicleApi.get(`/owner/${ownerId}`);
      console.log("response-veh", response);

      // ✅ FIX: Access response.data.data instead of response.data
      if (response.data?.success && response.data?.data) {
        setVehicles(response.data.data);
        setError(null);
      } else {
        setError("No data received from server");
        // Set empty arrays to prevent undefined errors
        setVehicles({
          verifiedVehicles: [],
          unverifiedVehicles: [],
        });
      }
    } catch (error) {
      console.error("API Request Error:", error);
      setError(
        error.response?.data?.message ||
          "Error fetching data. Please try again."
      );
      // Set empty arrays to prevent undefined errors
      setVehicles({
        verifiedVehicles: [],
        unverifiedVehicles: [],
      });
    } finally {
      console.log("data here", vehicles);
      setLoading(false);
    }
  };

  const VehicleCard = ({ vehicle, isVerified }) => (
    <Card
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        backgroundColor: colors.primary[400],
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
        position: "relative",
        overflow: "visible",
        mb: 3,
      }}
    >
      {/* Status Badge */}
      <Chip
        icon={isVerified ? <CheckCircle /> : <Warning />}
        label={isVerified ? "Verified" : "Pending Verification"}
        color={isVerified ? "success" : "warning"}
        sx={{
          position: "absolute",
          top: -10,
          right: 20,
          zIndex: 1,
        }}
      />

      {/* Vehicle Image */}
      <CardMedia
        component="img"
        sx={{
          width: { xs: "100%", sm: 200 },
          height: { xs: 200, sm: "100%" },
          objectFit: "cover",
        }}
        image={"rd.png"}
        alt={vehicle.name}
      />

      {/* Vehicle Details */}
      <CardContent sx={{ flex: 1, p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight="bold">
            {vehicle.name}
          </Typography>
          <Chip label={vehicle.Type} color="primary" size="small" />
        </Box>

        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <DirectionsBus color="primary" />
            <Typography variant="body2">
              {vehicle.vehicleNum.toUpperCase()}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Speed color="primary" />
            <Typography variant="body2">Last Speed: 65 km/h</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Schedule color="primary" />
            <Typography variant="body2">
              Total Trips: {vehicle.Trip?.length || 0}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOn color="primary" />
            <Typography variant="body2">Track Location</Typography>
          </Box>
        </Box>

        <Box display="flex" gap={2} mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/vehicle-individual/${vehicle.id}`)}
          >
            View Details
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              setSelectedVehicle(vehicle);
              setDialogOpen(true);
            }}
          >
            Quick View
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const VehicleQuickView = ({ vehicle, open, onClose }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <DirectionsBus />
          <Typography variant="h6">{vehicle?.name}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box p={2}>
          {/* Add a small map preview */}
          <Box
            sx={{
              height: 200,
              backgroundColor: colors.primary[400],
              borderRadius: 1,
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Map Preview (Coming Soon)
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" mb={2}>
                Vehicle Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <InfoRow
                  icon={<DirectionsBus />}
                  label="Vehicle Number"
                  value={vehicle?.vehicleNum}
                />
                <InfoRow icon={<Speed />} label="Type" value={vehicle?.Type} />
                <InfoRow
                  icon={<Schedule />}
                  label="Status"
                  value={vehicle?.isVerified ? "Verified" : "Pending"}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" mb={2}>
                Recent Activity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No recent activity
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title="Vehicle Fleet Management"
        subtitle="Monitor and manage your entire fleet"
      />

      {error && (
        <Box mb={4}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Stats Overview with null safety */}
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          sm: "1fr 1fr",
          md: "1fr 1fr 1fr 1fr",
        }}
        gap={3}
        mb={4}
      >
        <StatCard
          title="Total Vehicles"
          value={
            (vehicles.verifiedVehicles?.length || 0) +
            (vehicles.unverifiedVehicles?.length || 0)
          }
          icon={<DirectionsBus />}
          color={colors.primary[300]}
        />
        <StatCard
          title="Active Vehicles"
          value={vehicles.verifiedVehicles?.length || 0}
          icon={<CheckCircle />}
          color={colors.greenAccent[500]}
        />
        <StatCard
          title="Pending Verification"
          value={vehicles.unverifiedVehicles?.length || 0}
          icon={<Warning />}
          color={colors.redAccent[500]}
        />
        <StatCard
          title="Total Trips"
          value={calculateTotalTrips(vehicles)}
          icon={<Schedule />}
          color={colors.blueAccent[500]}
        />
      </Box>

      {/* Vehicles Lists with null safety */}
      <Box>
        {vehicles.verifiedVehicles?.length > 0 && (
          <>
            <Typography variant="h5" mb={3}>
              Active Vehicles ({vehicles.verifiedVehicles.length})
            </Typography>
            {vehicles.verifiedVehicles.map((vehicle) => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                isVerified={true}
              />
            ))}
          </>
        )}

        {vehicles.unverifiedVehicles?.length > 0 && (
          <>
            <Typography variant="h5" mb={3} mt={4}>
              Pending Verification ({vehicles.unverifiedVehicles.length})
            </Typography>
            {vehicles.unverifiedVehicles.map((vehicle) => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                isVerified={false}
              />
            ))}
          </>
        )}

        {/* No vehicles message */}
        {!vehicles.verifiedVehicles?.length &&
          !vehicles.unverifiedVehicles?.length &&
          !loading &&
          !error && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={8}
            >
              <DirectionsBus
                sx={{ fontSize: 80, color: colors.grey[500], mb: 2 }}
              />
              <Typography variant="h5" color="text.secondary" mb={1}>
                No Vehicles Found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Add your first vehicle to get started
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/vehicles")}
              >
                Add Vehicle
              </Button>
            </Box>
          )}
      </Box>

      {/* Quick View Dialog */}
      <VehicleQuickView
        vehicle={selectedVehicle}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
};

export default Vehicles;
